import React from 'react';
import { useLanguage } from './LanguageContext';
import { fmtAEDFull } from './portfolioMetrics';

/* ================================================================
   HOW THIS UNIT COMPARES — the answer to "am I doing well?"

   The valuation says what a unit is worth. This says whether that is
   good, by putting the unit's AED/sqft beside its building's median
   and its community's median, both computed from registered DLD sales
   in the last 12 months.

   Design rules, inherited from the valuation report deliberately:
     • never show a median built on fewer than `minComps` sales — say
       how many were found instead. A median of two is not a benchmark.
     • state the basis (bedroom-matched vs all unit types) rather than
       silently widening the pool to manufacture a number.
     • off-plan units are compared with off-plan sales only, and it says
       so — mixing ready and off-plan stock produces a meaningless figure.
     • trailing change is labelled as the last 3 months against the 3
       before. It is history. This screen makes no forecast.
   ================================================================ */

export interface BenchmarkScope {
  medianPsf: number | null;
  p25Psf?: number | null;
  p75Psf?: number | null;
  comps: number;
  basis: 'bedroom_matched' | 'all_unit_types' | string;
  sufficient: boolean;
  label: string;
  vsUnitPct: number | null;
}

export interface BenchmarkData {
  available: boolean;
  reason?: string;
  windowMonths: number;
  minComps: number;
  subject: {
    name: string;
    project: string;
    areaName: string;
    bedrooms: number | null;
    sizeSqft: number | null;
    offplan: boolean;
    pricePerSqft: number | null;
    pricePerSqftSource: 'dld_valuation' | 'purchase_price' | string;
  };
  building: BenchmarkScope;
  area: BenchmarkScope;
  trend: {
    building: { changePct: number; recentComps: number; priorComps: number } | null;
    area: { changePct: number; recentComps: number; priorComps: number } | null;
  };
  liquidity?: {
    buildingSales12m: number;
    buildingPerQuarter: number;
    areaSales12m: number;
    areaPerQuarter: number;
    windowMonths: number;
  };
  economics?: {
    available: boolean;
    reason?: string;
    annualRent?: number;
    value?: number;
    grossYieldPct?: number | null;
    netYieldPct?: number | null;
    serviceChargePsf?: number | null;
    serviceChargeAnnual?: number | null;
    serviceChargeSource?: string | null;
    valueBasis?: string;
  };
  areaRent?: {
    medianRentPsf: number | null;
    contracts: number;
    basis?: string;
    sufficient: boolean;
  };
  supply?: {
    available: boolean;
    reason?: string;
    areaName?: string;
    horizonMonths?: number;
    projects?: number;
    units?: number;
    nextProjects?: { project: string; developer: string; units: number | null; completionDate: string; completionPercentage: number }[];
  };
}

interface Props {
  data?: BenchmarkData | null;
  loading?: boolean;
}

const GOLD = '#b08d3f';
const INK = '#1a1a2e';
const MUTED = '#6f6960';
const UP = '#16a34a';
const DOWN = '#dc2626';

/** One horizontal bar row. Widths are relative to the largest value shown. */
const Bar: React.FC<{
  label: string;
  sub?: string;
  value: number | null;
  max: number;
  strong?: boolean;
  color: string;
}> = ({ label, sub, value, max, strong, color }) => {
  const pct = value && max > 0 ? Math.max(4, Math.round((value / max) * 100)) : 0;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
        <span style={{ fontSize: 13, fontWeight: strong ? 700 : 600, color: strong ? INK : MUTED }}>
          {label}
        </span>
        <span style={{ fontSize: 13.5, fontWeight: strong ? 800 : 700, color: strong ? INK : MUTED, whiteSpace: 'nowrap' }}>
          {value ? `AED ${fmtAEDFull(value)}` : '—'}
        </span>
      </div>
      <div style={{ height: 8, background: 'rgba(120,113,90,0.12)', borderRadius: 99, marginTop: 5, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 99, transition: 'width .6s ease' }} />
      </div>
      {sub && <div style={{ fontSize: 11.5, color: MUTED, marginTop: 4 }}>{sub}</div>}
    </div>
  );
};

const BenchmarkPanel: React.FC<Props> = ({ data, loading }) => {
  const { t } = useLanguage();

  if (loading) {
    return (
      <div className="pr-panel">
        <p className="pr-label" style={{ marginBottom: 10 }}>{t('bmTitle')}</p>
        <div className="pt-skel" style={{ height: 120, borderRadius: 10 }} />
      </div>
    );
  }

  if (!data || !data.available) {
    return (
      <div className="pr-panel">
        <p className="pr-label" style={{ marginBottom: 8 }}>{t('bmTitle')}</p>
        <p className="pr-tr-sub">{t('bmUnavailable')}</p>
      </div>
    );
  }

  const { subject, building, area, trend, windowMonths, minComps } = data;
  const unitPsf = subject.pricePerSqft;

  const values = [unitPsf, building.medianPsf, area.medianPsf].filter((n): n is number => !!n);
  const max = values.length ? Math.max(...values) : 0;

  const basisLabel = (b: string) =>
    b === 'bedroom_matched' ? t('bmBasisBeds') : t('bmBasisAllTypes');

  const scopeSub = (s: BenchmarkScope) =>
    s.sufficient
      ? `${s.comps} ${s.comps === 1 ? t('bmSale') : t('bmSales')} · ${basisLabel(s.basis)}`
      : `${t('bmTooFew')} (${s.comps})`;

  /** Headline verdict against the building, which is the fairest comparison. */
  const verdict = (() => {
    if (!unitPsf || !building.sufficient || building.vsUnitPct === null) return null;
    const p = building.vsUnitPct;
    const near = Math.abs(p) < 2;
    return {
      text: near
        ? t('bmVerdictInLine')
        : `${Math.abs(p).toFixed(1)}% ${p > 0 ? t('bmVerdictAbove') : t('bmVerdictBelow')}`,
      color: near ? GOLD : (p > 0 ? UP : DOWN),
    };
  })();

  const TrendChip: React.FC<{ label: string; d: { changePct: number; recentComps: number; priorComps: number } | null }> =
    ({ label, d }) => {
      if (!d) return null;
      const up = d.changePct >= 0;
      return (
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 10px',
          borderRadius: 99, background: 'rgba(120,113,90,0.07)', fontSize: 12, color: MUTED,
        }}>
          <span style={{ fontWeight: 700, color: up ? UP : DOWN }}>
            {up ? '▲' : '▼'} {Math.abs(d.changePct).toFixed(1)}%
          </span>
          <span>{label}</span>
        </div>
      );
    };

  return (
    <div className="pr-panel">
      <p className="pr-label" style={{ marginBottom: 4 }}>{t('bmTitle')}</p>
      <p className="pr-tr-sub" style={{ margin: '0 0 14px' }}>
        {t('bmSubtitlePre')} {windowMonths} {t('bmSubtitlePost')}
      </p>

      {verdict && (
        <div style={{ marginBottom: 16 }}>
          <span style={{ fontSize: 22, fontWeight: 800, color: verdict.color }}>{verdict.text}</span>
          <span style={{ fontSize: 13, color: MUTED, marginLeft: 8 }}>{t('bmVsBuilding')}</span>
        </div>
      )}

      <Bar
        label={t('bmThisUnit')}
        sub={subject.pricePerSqftSource === 'purchase_price' ? t('bmFromPurchase') : t('bmFromValuation')}
        value={unitPsf}
        max={max}
        strong
        color={GOLD}
      />
      <Bar
        label={building.label || t('bmBuilding')}
        sub={scopeSub(building)}
        value={building.sufficient ? building.medianPsf : null}
        max={max}
        color="rgba(26,26,46,0.55)"
      />
      <Bar
        label={area.label || t('bmCommunity')}
        sub={scopeSub(area)}
        value={area.sufficient ? area.medianPsf : null}
        max={max}
        color="rgba(26,26,46,0.3)"
      />

      {(trend.building || trend.area) && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
          <TrendChip label={`${building.label || t('bmBuilding')} · ${t('bm3mVs3m')}`} d={trend.building} />
          <TrendChip label={`${area.label || t('bmCommunity')} · ${t('bm3mVs3m')}`} d={trend.area} />
        </div>
      )}

      {/* ---------- what it earns: gross vs net after the real service charge ----------
          Net yield is the number an owner can actually decide on, and it is only
          possible because Mollak service charges are synced. Shown only when both
          rent evidence and a value exist; net only when the building's charge is
          known — never by assuming a percentage. */}
      {data.economics?.available && (
        <div style={{
          marginTop: 18, paddingTop: 16, borderTop: '1px solid rgba(120,113,90,0.16)',
        }}>
          <p className="pr-label" style={{ marginBottom: 10 }}>{t('bmEarnsTitle')}</p>
          <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 11.5, color: MUTED, fontWeight: 600 }}>{t('bmGrossYield')}</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: INK }}>
                {data.economics.grossYieldPct != null ? `${data.economics.grossYieldPct.toFixed(2)}%` : '—'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11.5, color: MUTED, fontWeight: 600 }}>{t('bmNetYield')}</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: data.economics.netYieldPct != null ? GOLD : MUTED }}>
                {data.economics.netYieldPct != null ? `${data.economics.netYieldPct.toFixed(2)}%` : '—'}
              </div>
            </div>
          </div>
          <p className="pr-tr-sub" style={{ margin: '8px 0 0' }}>
            {data.economics.serviceChargePsf != null
              ? `${t('bmAfterServiceCharge')} AED ${fmtAEDFull(data.economics.serviceChargePsf)}/sqft/${t('bmYear')}`
                + (data.economics.serviceChargeAnnual ? ` · AED ${fmtAEDFull(data.economics.serviceChargeAnnual)}/${t('bmYear')}` : '')
                + (data.economics.serviceChargeSource ? ` · ${t('bmSource')}: ${data.economics.serviceChargeSource}` : '')
              : t('bmNoServiceCharge')}
          </p>
        </div>
      )}

      {/* ---------- liquidity: price without turnover is half the picture ---------- */}
      {data.liquidity && data.liquidity.buildingSales12m > 0 && (
        <div style={{ marginTop: 16 }}>
          <p className="pr-label" style={{ marginBottom: 6 }}>{t('bmLiquidityTitle')}</p>
          <p className="pr-tr-sub" style={{ margin: 0 }}>
            {data.liquidity.buildingSales12m} {data.liquidity.buildingSales12m === 1 ? t('bmSale') : t('bmSales')}
            {' '}{t('bmInThisBuilding')} · {data.liquidity.buildingPerQuarter} {t('bmPerQuarter')}
            {data.liquidity.areaSales12m > 0 && (
              <> · {data.liquidity.areaSales12m} {t('bmInTheCommunity')}</>
            )}
          </p>
        </div>
      )}

      {/* ---------- what the community rents at ---------- */}
      {data.areaRent?.sufficient && (
        <div style={{ marginTop: 14 }}>
          <p className="pr-label" style={{ marginBottom: 6 }}>{t('bmAreaRentTitle')}</p>
          <p className="pr-tr-sub" style={{ margin: 0 }}>
            AED {fmtAEDFull(data.areaRent.medianRentPsf as number)}/sqft/{t('bmYear')}
            {' · '}{data.areaRent.contracts} {t('bmEjariContracts')}
          </p>
        </div>
      )}

      {/* ---------- supply pipeline ----------
          Registered future stock in this community. Deliberately framed as
          inventory, never as a price prediction: the investor draws the
          conclusion. Shown only when the registry actually has projects with
          a completion date inside the horizon. */}
      {data.supply?.available && (data.supply.projects || 0) > 0 && (
        <div style={{ marginTop: 16 }}>
          <p className="pr-label" style={{ marginBottom: 6 }}>{t('bmSupplyTitle')}</p>
          <p className="pr-tr-sub" style={{ margin: 0 }}>
            {data.supply.units ? `${data.supply.units.toLocaleString('en-AE')} ${t('bmSupplyUnits')} ` : ''}
            {t('bmSupplyAcross')} {data.supply.projects} {data.supply.projects === 1 ? t('bmSupplyProject') : t('bmSupplyProjects')}
            {' '}{t('bmSupplyWithin')} {data.supply.horizonMonths} {t('bmSubtitlePost')}
            {data.supply.areaName ? ` · ${data.supply.areaName}` : ''}
          </p>
          {/* The DDADS registry stores many project names in Arabic only
              ("أوشن تاور"). Showing those to a DACH investor reads as broken
              data, and we have no English equivalent for them — so name only
              the Latin-script ones and let the totals carry the rest. */}
          {(() => {
            const named = (data.supply.nextProjects || [])
              .filter(pr => /[A-Za-z]/.test(pr.project || ''))
              .slice(0, 3);
            if (!named.length) return null;
            return (
              <p className="pr-foot" style={{ marginTop: 6 }}>
                {t('bmSupplyNext')}: {named.map(pr =>
                  `${pr.project}${pr.units ? ` (${pr.units})` : ''}`).join(' · ')}
              </p>
            );
          })()}
        </div>
      )}

      <p className="pr-foot" style={{ marginTop: 14 }}>
        {subject.offplan ? t('bmOffplanNote') : t('bmReadyNote')}{' '}
        {t('bmMinCompsPre')} {minComps} {t('bmMinCompsPost')}
      </p>
    </div>
  );
};

export default BenchmarkPanel;
